/**
 * BaseWebGPUVisualizer - Base class for WebGPU (Three.js) visualizers
 *
 * Provides common functionality:
 * - WebGPU renderer initialization
 * - Scene and camera setup
 * - Animation loop management (browser + export modes)
 * - Frequency data processing with smoothing
 * - Configuration management
 * - Resource disposal
 * - Enhanced audio features (optional, lazy-evaluated)
 *
 * Subclasses must implement:
 * - static name: string
 * - static displayName: string
 * - static getConfigUI(): ConfigItem[]
 * - getObjectCount(): number
 * - initObjects(): void
 * - updateObjects(amplitudes: number[], features?: AudioFeatures): void
 * - disposeObjects(): void
 *
 * Subclasses may override:
 * - getDefaultConfig(): object
 * - initScene(): void (called after base scene/camera setup)
 * - onConfigChange(changed: object): void
 */

import * as THREE from 'three/webgpu';
import { mapFrequencyData, smoothAmplitudes, extractConfigDefaults } from './audio-utils.js';
import {
  getBackgroundColor,
  getBackgroundVersion,
  isSimpleBackground,
  renderBackgroundToCanvas
} from '../app/background-controller.js';
import { ResourceRegistry, ResourceAuditor } from './resource-registry.js';
import { WebGPULyricsOverlay } from './webgpu-lyrics-overlay.js';
import { applyAudioFeatureMixin } from './audio-feature-mixin.js';

export class BaseWebGPUVisualizer {
  static contextType = 'webgpu';
  static isWebGL = true; // For compatibility with existing code

  // Subclasses MUST override these
  static name = '';
  static displayName = '';

  /**
   * @param {HTMLCanvasElement} canvas - The canvas element to render to
   * @param {object} options - Configuration options
   */
  constructor(canvas, options = {}) {
    this.canvas = canvas;

    // State
    this.disposed = false;
    this.ready = false;
    this.paused = false;
    this.suspended = false;
    this.exportMode = options.exportMode || false;
    this.animationId = null;
    this.lastFrequencyData = null;

    // Pre-allocated buffers to avoid per-frame GC
    this.emptyFrequencyData = new Uint8Array(1024);
    this.previousAmplitudes = [];
    this._freqBuffer = null;
    this._previousSemitones = null; // For log bins smoothing

    // Background texture state for gradient/pattern/image backgrounds
    this._backgroundTexture = null;
    this._backgroundVersion = -1;

    // Lyrics overlay for GPU-side compositing (export mode)
    this._lyricsOverlay = null;

    // Resource registry for tracking GPU resources
    const registryLabel = `${this.constructor.name || 'WebGPUVisualizer'}-${this.exportMode ? 'export' : 'preview'}`;
    this.resources = new ResourceRegistry(registryLabel);
    ResourceAuditor.registerRegistry(this.resources);

    // Extract defaults from getConfigUI() if available
    const uiDefaults = this.constructor.getConfigUI
      ? extractConfigDefaults(this.constructor.getConfigUI())
      : {};

    // Merge configuration: base defaults -> UI defaults -> subclass defaults -> user options
    this.config = {
      colorStart: '#00ffff',
      colorEnd: '#ff00ff',
      backgroundColor: '#0a0a0a',
      smoothing: 0.8,
      cameraDistance: 15,
      ...uiDefaults,
      ...this.getDefaultConfig(),
      ...options
    };

    // Start async initialization
    this.init();
  }

  /**
   * Returns subclass-specific default configuration.
   * Override in subclass to provide visualizer-specific defaults.
   * @returns {object}
   */
  getDefaultConfig() {
    return {};
  }

  /**
   * Async initialization - sets up renderer, scene, and objects
   */
  async init() {
    try {
      await this.initRenderer();

      // Check if disposed during async init
      if (this.disposed) return;

      // Create scene and camera
      this.scene = new THREE.Scene();
      // Set initial background (from background-controller)
      this.updateBackground();

      const aspect = this.canvas.width / this.canvas.height;
      this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 1000);
      this.camera.position.z = this.config.cameraDistance;

      // Allow subclass to add scene-specific setup (lights, environment, etc.)
      this.initScene();

      // Create visualization objects
      this.initObjects();

      this.ready = true;

      // Start animation loop (browser mode only)
      if (!this.exportMode && !this.disposed) {
        this.startAnimationLoop();
      }
    } catch (error) {
      if (!this.disposed) {
        console.error(`${this.constructor.name} initialization error:`, error);
        this.initializationError = error;
        this.disposed = true;
        throw error;
      }
    }
  }

  /**
   * Initialize WebGPU renderer
   */
  async initRenderer() {
    this.renderer = this.resources.track(
      new THREE.WebGPURenderer({
        canvas: this.canvas,
        antialias: true,
        alpha: false
      }),
      { name: 'WebGPURenderer', type: 'THREE.WebGPURenderer' }
    );

    // Set size (don't update style in export mode)
    const updateStyle = !this.exportMode;
    this.renderer.setSize(this.canvas.width, this.canvas.height, updateStyle);

    // Pixel ratio: 1 for export (exact resolution), capped at 2 for browser
    const pixelRatio = this.exportMode
      ? 1
      : (typeof window !== 'undefined' && window.devicePixelRatio
        ? Math.min(window.devicePixelRatio, 2)
        : 1);
    this.renderer.setPixelRatio(pixelRatio);

    // Wait for WebGPU initialization
    await this.renderer.init();

    // Track the WebGPU device for explicit cleanup
    try {
      const device = this.renderer?.backend?.device;
      if (device) {
        this.resources.track(device, {
          name: 'WebGPUDevice',
          type: 'GPUDevice',
          dispose: () => device.destroy()
        });
      }
    } catch {
      // Device access may vary across Three.js versions
    }

    // Color management
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
  }

  /**
   * Updates the scene background.
   * Supports two sources: per-visualizer config (backgroundColor) and the global
   * background controller (solid, gradient, pattern, image). Last write wins —
   * if the user changes the visualizer's background color, it takes effect;
   * if they then change the global background, that takes priority instead.
   */
  updateBackground() {
    if (!this.scene) return;

    const currentVersion = getBackgroundVersion();
    const globalChanged = this._backgroundVersion !== currentVersion;

    // If global background was updated, it takes priority
    if (globalChanged) {
      this._useOwnBackground = false;
      this._backgroundVersion = currentVersion;

      if (isSimpleBackground()) {
        if (this._backgroundTexture) {
          this.resources.dispose(this._backgroundTexture);
          this._backgroundTexture = null;
        }
        this.scene.background = new THREE.Color(getBackgroundColor());
      } else {
        const bgCanvas = renderBackgroundToCanvas(this.canvas.width, this.canvas.height);
        if (this._backgroundTexture) {
          this.resources.dispose(this._backgroundTexture);
        }
        this._backgroundTexture = this.resources.track(
          new THREE.CanvasTexture(bgCanvas),
          { name: 'BackgroundTexture', type: 'THREE.CanvasTexture' }
        );
        this._backgroundTexture.colorSpace = THREE.SRGBColorSpace;
        this.scene.background = this._backgroundTexture;
      }
      return;
    }

    // Apply per-visualizer background color if user changed it via config
    if (this._useOwnBackground && this._appliedBgColor !== this.config.backgroundColor) {
      if (this._backgroundTexture) {
        this.resources.dispose(this._backgroundTexture);
        this._backgroundTexture = null;
      }
      this.scene.background = new THREE.Color(this.config.backgroundColor);
      this._appliedBgColor = this.config.backgroundColor;
    }
  }

  /**
   * Sets or updates the lyrics overlay canvas for GPU-side compositing.
   * This avoids GPU readback by rendering lyrics as a texture overlay in the scene.
   * @param {OffscreenCanvas|HTMLCanvasElement|null} lyricsCanvas - Canvas with rendered lyrics, or null to clear
   */
  setLyricsOverlay(lyricsCanvas) {
    if (this.disposed) return;

    if (!lyricsCanvas) {
      // Clear overlay
      if (this._lyricsOverlay) {
        this._lyricsOverlay.clear();
      }
      return;
    }

    // Create overlay on first use
    if (!this._lyricsOverlay) {
      this._lyricsOverlay = new WebGPULyricsOverlay(
        this.canvas.width,
        this.canvas.height
      );
    }

    // Update the texture from the lyrics canvas
    this._lyricsOverlay.updateTexture(lyricsCanvas);
  }

  /**
   * Clears the lyrics overlay.
   */
  clearLyricsOverlay() {
    if (this._lyricsOverlay) {
      this._lyricsOverlay.clear();
    }
  }

  /**
   * Called after base scene/camera setup. Override to add lights, environment, etc.
   */
  initScene() {}

  /**
   * Updates camera position and orientation. Override for custom camera behavior.
   * Default implementation sets camera position based on cameraDistance config.
   * Override this method to use OrbitControls or custom camera animation.
   */
  updateCamera() {
    this.camera.position.set(0, 0, this.config.cameraDistance);
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Initialize visualization objects. MUST be implemented by subclass.
   */
  initObjects() {
    throw new Error('Subclass must implement initObjects()');
  }

  /**
   * Returns the number of visualization objects.
   * MUST be implemented by subclass.
   * @returns {number}
   */
  getObjectCount() {
    throw new Error('Subclass must implement getObjectCount()');
  }

  /**
   * Updates visualization objects based on amplitude values.
   * MUST be implemented by subclass.
   * @param {Array<number>} amplitudes - Smoothed amplitude values (0-1) for each object
   * @param {AudioFeatures} [features] - Optional enhanced audio features (lazy-evaluated)
   */
  updateObjects(amplitudes, features) {
    throw new Error('Subclass must implement updateObjects()');
  }

  /**
   * Disposes of visualization objects. MUST be implemented by subclass.
   */
  disposeObjects() {
    throw new Error('Subclass must implement disposeObjects()');
  }

  /**
   * Map 96 semitone bins to element count with smoothing (for log bins mode)
   * @param {Float32Array} semitones - 96 semitone bin values
   * @param {number} count - Number of elements to map to
   * @returns {Array<number>} Mapped and smoothed values
   * @private
   */
  _mapSemitonesToElements(semitones, count) {
    // Initialize buffer if needed
    if (!this._previousSemitones || this._previousSemitones.length !== count) {
      this._previousSemitones = new Float32Array(count);
    }

    const result = new Array(count);
    const binsPerElement = semitones.length / count;
    const smoothing = this.config.smoothing;

    for (let i = 0; i < count; i++) {
      const start = Math.floor(i * binsPerElement);
      const end = Math.floor((i + 1) * binsPerElement);
      let rawValue = 0;
      if (end > start) {
        let sum = 0;
        for (let j = start; j < end; j++) {
          sum += semitones[j];
        }
        rawValue = sum / (end - start);
      }

      // Apply smoothing
      result[i] = this._previousSemitones[i] * smoothing + rawValue * (1 - smoothing);
      this._previousSemitones[i] = result[i];
    }
    return result;
  }

  /**
   * Starts the internal animation loop (browser mode only)
   */
  startAnimationLoop() {
    if (typeof requestAnimationFrame === 'undefined') {
      return; // Running in Worker or non-browser context
    }

    const animate = () => {
      if (this.disposed || !this.ready || this.paused) return;

      // Schedule next frame first for smooth timing
      this.animationId = requestAnimationFrame(animate);

      // Use cached frequency data and features from last draw() call
      const frequencyData = this.lastFrequencyData || this.emptyFrequencyData;
      this.drawFrame(frequencyData, this._currentFeatures); // Non-awaited for async GPU operations
    };
    animate();
  }

  /**
   * Pause the visualizer animation loop.
   * Used during export to free GPU resources.
   */
  pause() {
    this.paused = true;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  /**
   * Resume the visualizer animation loop after pause.
   */
  resume() {
    this.paused = false;
    if (!this.exportMode && !this.disposed && this.ready) {
      this.startAnimationLoop();
    }
  }

  /**
   * Suspend the visualizer and release GPU resources.
   * More aggressive than pause() - disposes the renderer entirely.
   * Use for long operations (like export) where GPU resources are needed elsewhere.
   * Call restore() to recreate the renderer and resume.
   */
  async suspend() {
    this.paused = true;
    this.suspended = true;

    // Stop animation loop
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Log resources before suspension for debugging
    if (this.resources.size > 0) {
      console.log(`[${this.constructor.name}] Suspending with ${this.resources.size} tracked resources`);
    }

    // Dispose renderer via registry to free GPU resources
    // Keep scene, camera, and objects in memory for quick restoration
    if (this.renderer) {
      this.resources.dispose(this.renderer);
      this.renderer = null;
    }

    // Dispose background texture via registry (will be recreated on restore)
    if (this._backgroundTexture) {
      this.resources.dispose(this._backgroundTexture);
      this._backgroundTexture = null;
    }
    this._backgroundVersion = -1;
  }

  /**
   * Restore the visualizer after suspend().
   * Recreates the renderer and resumes animation.
   */
  async restore() {
    if (!this.suspended || this.disposed) return;

    // Recreate renderer
    await this.initRenderer();

    // Re-apply background
    this.updateBackground();

    this.suspended = false;
    this.paused = false;

    // Resume animation loop
    if (!this.exportMode && !this.disposed && this.ready) {
      this.startAnimationLoop();
    }
  }

  /**
   * Called from external animation loop with frequency data.
   * In export mode, returns a Promise to allow frame synchronization.
   * @param {Uint8Array} frequencyData - Frequency data from AnalyserNode
   * @param {AudioFeatures} [features] - Optional enhanced audio features (lazy-evaluated)
   * @returns {Promise|undefined}
   */
  draw(frequencyData, features = null) {
    if (this.disposed) return;
    this.lastFrequencyData = frequencyData;
    this._currentFeatures = features;

    if (this.exportMode) {
      // Return Promise so exporter can await frame completion
      return this.drawFrame(frequencyData, features);
    }
  }

  /**
   * Renders a single frame.
   * @param {Uint8Array} frequencyData - Frequency data
   * @param {AudioFeatures} [features] - Optional enhanced audio features
   */
  async drawFrame(frequencyData, features = null) {
    if (this.disposed || !this.renderer || !this.ready) return;

    const count = this.getObjectCount();

    // Ensure buffers are properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== count) {
      this._freqBuffer = new Array(count).fill(0);
    }
    if (this.previousAmplitudes.length !== count) {
      this.previousAmplitudes = new Array(count).fill(0);
    }

    // Ensure frequency data (use empty buffer if none provided)
    if (!frequencyData || frequencyData.length === 0) {
      frequencyData = this.emptyFrequencyData;
    }

    // Map frequency data to object count and apply smoothing
    const raw = mapFrequencyData(frequencyData, count, this._freqBuffer);
    let amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Use semitones (log bins) if enabled and features available
    if (this.config.useLogBins && features?.pitch) {
      amplitudes = this._mapSemitonesToElements(features.pitch.semitones(), count);
    }

    // Note: _currentFeatures is already set by draw() - no need to set here

    // Update background from global state (handles solid, gradient, pattern, image)
    this.updateBackground();

    // Update camera (subclass can override for custom behavior like OrbitControls)
    this.updateCamera();

    // Let subclass update its objects
    this.updateObjects(amplitudes, features);

    // Render main scene with WebGPU (async)
    await this.renderer.renderAsync(this.scene, this.camera);

    // Render lyrics overlay on top if present (GPU-side compositing)
    if (this._lyricsOverlay && this._lyricsOverlay.texture) {
      // Disable autoClear to preserve main scene
      const prevAutoClear = this.renderer.autoClear;
      this.renderer.autoClear = false;

      await this._lyricsOverlay.render(this.renderer);

      // Restore autoClear
      this.renderer.autoClear = prevAutoClear;
    }
  }

  // Audio feature convenience getters are added via mixin (see end of file)

  /**
   * Updates configuration and handles common config changes.
   * @param {object} newConfig - New configuration values to merge
   */
  setConfig(newConfig) {
    // Track what actually changed
    const changed = {};
    for (const key in newConfig) {
      if (this.config[key] !== newConfig[key]) {
        changed[key] = newConfig[key];
      }
    }

    // Merge new config
    this.config = { ...this.config, ...newConfig };

    // If not ready yet, config will be applied when init completes
    if (!this.ready) return;

    // Handle common config changes
    if (changed.backgroundColor) {
      // Flag that user changed per-visualizer background (takes effect next frame)
      this._useOwnBackground = true;
      this._appliedBgColor = null;
    }
    if (changed.cameraDistance && this.camera) {
      this.camera.position.z = this.config.cameraDistance;
    }

    // Notify subclass of changes
    if (Object.keys(changed).length > 0) {
      this.onConfigChange(changed);
    }
  }

  /**
   * Called when configuration changes. Override to handle specific config updates.
   * @param {object} changed - Object containing only the changed keys and their new values
   */
  onConfigChange(changed) {}

  /**
   * Clears visualization state and re-renders.
   */
  async clear() {
    if (!this.ready || !this.renderer) return;
    this.previousAmplitudes.fill(0);
    await this.renderer.renderAsync(this.scene, this.camera);
  }

  /**
   * Disposes of all resources.
   */
  dispose() {
    this.disposed = true;
    this.ready = false;

    // Cancel animation loop
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Dispose visualization objects (subclass implementation)
    // This should untrack/dispose subclass-specific resources
    this.disposeObjects();

    // Dispose lyrics overlay if present
    if (this._lyricsOverlay) {
      this._lyricsOverlay.dispose();
      this._lyricsOverlay = null;
    }

    // Clean scene children
    if (this.scene) {
      while (this.scene.children.length > 0) {
        this.scene.remove(this.scene.children[0]);
      }
      this.scene = null;
    }

    this.camera = null;
    this._backgroundTexture = null;

    // Safely dispose renderer before registry cleanup
    // Three.js WebGPURenderer.dispose() can fail if not fully initialized
    if (this.renderer) {
      try {
        // Check if renderer is in a valid state (has info object after init)
        if (this.renderer.info) {
          this.renderer.dispose();
        }
      } catch {
        // Ignore disposal errors for partially-initialized renderer
      }
      // Untrack so registry doesn't try to dispose again
      this.resources.untrack(this.renderer);
      this.renderer = null;
    }

    // Dispose all tracked resources via registry (handles ordering)
    // This disposes device, background texture, and any subclass-tracked resources
    this.resources.disposeAll({ log: true });

    // Verify cleanup in development
    this.resources.verify();

    // Unregister from global auditor
    ResourceAuditor.unregisterRegistry(this.resources);
  }

  /**
   * Debug helper: dump tracked resources to console.
   * Call before export to verify clean state.
   */
  debugResources() {
    console.log(`[${this.constructor.name}] Resource debug:`);
    this.resources.dumpTop({ limit: 50 });
  }

  /**
   * Interpolates between the configured start and end colors.
   * Returns a THREE.Color object for use with Three.js materials.
   * @param {number} t - Interpolation factor (0-1)
   * @returns {THREE.Color}
   */
  lerpColor(t) {
    const c1 = new THREE.Color(this.config.colorStart);
    const c2 = new THREE.Color(this.config.colorEnd);
    return c1.lerp(c2, t);
  }
}

// Apply audio feature convenience getters (beatDetected, bassEnergy, etc.)
applyAudioFeatureMixin(BaseWebGPUVisualizer);
